import React from 'react';
import { Input } from '../../../../shared/components/Input';
import { Select } from '../../../../shared/components/Select';
import { Checkbox } from '../../../../shared/components/Checkbox';
import { ModuleState, ARRIVAL_TYPES } from './moduleDefs';
import { ModulePriceBreakdownPanel } from './ModulePriceBreakdownPanel';
import { ModuleBreakdown } from '../../utils/pricingSheetData';
import { PipeFitterRatesDrawer } from './PipeFitterRatesDrawer';

/**
 * PipeFitter detail pane. Three sections drive the price: scanning (CRC service
 * per pipe, or BYOD onboarding), two per-joint reports, and the OptiFit / OmniFit
 * algorithms per joint. Each report and algorithm is independently off / per-joint
 * / flat (reports also free-hook). The rates live in the Edit cost assumptions
 * drawer; this pane carries only the per-proposal selections.
 */

interface Props {
  module: ModuleState;
  onChange: (patch: Partial<ModuleState>) => void;
  breakdown?: ModuleBreakdown | null;
  pipefitterConfig?: Record<string, number>;
  nreConfig?: Record<string, number>;
}

const REPORT_MODE_OPTIONS = [
  { value: 'off', label: 'Off' },
  { value: 'perJoint', label: 'Per joint' },
  { value: 'flat', label: 'Flat fee' },
  { value: 'free', label: 'Free hook' },
];

const ALGO_MODE_OPTIONS = [
  { value: 'perJoint', label: 'Per joint' },
  { value: 'flat', label: 'Flat fee' },
  { value: 'gainShare', label: 'Gain-share (savings)' },
];

// Per-algorithm gain-share savings inputs.
const GAIN_FIELDS = [
  { suffix: 'ManualMin', label: 'Manual fit-up (min)', tooltip: 'Minutes to fit up one joint by hand.' },
  { suffix: 'ToolMin', label: 'Tool fit-up (min)', tooltip: 'Minutes to fit up one joint with the algorithm.' },
  { suffix: 'ValuePerHour', label: 'Firing-line $/hr', tooltip: 'Value of an hour of firing-line time. Time saved x this = value per joint.' },
];

const num = (v: string): number | undefined => (v === '' ? undefined : Math.max(0, Number(v) || 0));

interface ReportRowProps {
  label: string;
  mode: string;
  flat: number | undefined;
  onMode: (mode: string) => void;
  onFlat: (value: number | undefined) => void;
}

const PricedRow: React.FC<ReportRowProps & { idPrefix: string; modeOptions: { value: string; label: string }[] }> = ({
  label,
  mode,
  flat,
  onMode,
  onFlat,
  idPrefix,
  modeOptions,
}) => (
  <div className="flex flex-wrap items-end gap-4">
    <div className="w-44">
      <span className="block text-sm font-medium text-text-grey">{label}</span>
    </div>
    <div className="w-40">
      <Select label="" value={mode} options={modeOptions} onChange={onMode} />
    </div>
    {mode === 'flat' && (
      <div className="w-40">
        <Input
          id={`${idPrefix}-flat`}
          label="Flat $"
          type="number"
          min={0}
          value={flat ?? ''}
          onChange={(e) => onFlat(num(e.target.value))}
          placeholder="0"
        />
      </div>
    )}
  </div>
);

export const PipeFitterPane: React.FC<Props> = ({ module, onChange, breakdown, pipefitterConfig, nreConfig }) => {
  const [ratesOpen, setRatesOpen] = React.useState(false);
  const options = module.options ?? {};
  const overrides = module.overrides ?? {};

  const setOption = (key: string, value: unknown) =>
    onChange({ options: { ...options, [key]: value } });

  const setOverride = (key: string, value: number | undefined) => {
    const next = { ...overrides };
    if (value === undefined || Number.isNaN(value)) delete next[key];
    else next[key] = value;
    onChange({ overrides: next });
  };

  const scanMode = options.scanningMode as string | undefined;
  const overrideCount = Object.keys(overrides).length;

  // An algorithm is on when its mode is a real pricing mode; the checkbox flips
  // it between 'off' and the default per-joint pricing.
  const algoOn = (modeKey: string) => {
    const mode = options[modeKey];
    return mode === 'perJoint' || mode === 'flat' || mode === 'gainShare';
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-xl font-heading font-semibold text-blueprint-blue">PipeFitter</h2>
          <p className="mt-1 text-sm text-heritage-grey">Scanning, reporting, and the fit-up algorithms.</p>
        </div>
        <button
          type="button"
          onClick={() => setRatesOpen(true)}
          className="rounded-lg border border-[#E2E5EA] px-3 py-2 text-sm font-medium text-text-grey hover:bg-page-bg"
        >
          Edit cost assumptions
          {overrideCount > 0 && (
            <span className="ml-1.5 rounded-full bg-primary px-1.5 py-0.5 text-xs text-white">{overrideCount}</span>
          )}
        </button>
      </div>

      {/* 1. Scanning */}
      <div className="border-t border-[#E2E5EA] pt-5">
        <h3 className="text-base font-medium text-blueprint-blue">1. Scanning service</h3>
        <p className="mt-0.5 text-sm text-heritage-grey">Choose how the pipe-end scans are delivered.</p>
        <div className="mt-3 space-y-2">
          <Checkbox
            id="pf-scan-service"
            label="CRCE service (our field crew scans the pipe ends, priced per pipe)"
            tooltip="A CRC field crew runs the scanning, priced per pipe plus a one-time mobilization."
            checked={scanMode === 'service'}
            onChange={(e) => setOption('scanningMode', e.target.checked ? 'service' : undefined)}
          />
          <Checkbox
            id="pf-scan-byod"
            label="BYOD (customer brings their own scans, one-time onboarding NRE)"
            tooltip="The customer provides the scan data; only a one-time onboarding NRE applies."
            checked={scanMode === 'byod'}
            onChange={(e) => setOption('scanningMode', e.target.checked ? 'byod' : undefined)}
          />

          {scanMode === 'byod' && (
            <div className="flex flex-wrap items-start gap-4 rounded-md border border-[#E2E5EA] bg-page-bg p-3">
              <div className="w-64">
                <Select
                  label="How will the data be delivered?"
                  tooltip="The delivery format sets the ingestion difficulty: a clean API feed onboards cheaply, scanned PDFs and handwritten paper cost more."
                  value={(options.deliveryFormat as string) ?? ''}
                  options={ARRIVAL_TYPES}
                  onChange={(v) => setOption('deliveryFormat', v)}
                />
                <p className="mt-1.5 text-xs text-heritage-grey">
                  A harder delivery format raises the cost-modeled onboarding NRE.
                </p>
              </div>
              <div className="w-44">
                <Input
                  id="pf-byod-nre-override"
                  label="NRE override $"
                  tooltip="Set a fixed onboarding NRE for this proposal, replacing the cost-modeled value. Leave blank to use the cost model."
                  type="number"
                  min={0}
                  value={(options.byodNreOverride as number) ?? ''}
                  onChange={(e) => setOption('byodNreOverride', num(e.target.value))}
                  placeholder="auto"
                />
              </div>
            </div>
          )}

          {scanMode && (
            <div className="w-56 pt-1">
              <Input
                id="pf-pipes"
                label="How many pipes?"
                tooltip="The project quantity. Drives the per-pipe scanning and the per-joint reporting and algorithm lines."
                type="number"
                min={0}
                value={module.volume ?? ''}
                onChange={(e) => onChange({ volume: num(e.target.value) })}
                placeholder="0"
              />
            </div>
          )}
        </div>
      </div>

      {/* 2. Reporting */}
      <div className="border-t border-[#E2E5EA] pt-5">
        <h3 className="text-base font-medium text-blueprint-blue">2. Reporting</h3>
        <p className="mt-0.5 text-sm text-heritage-grey">Two reports, each off / per joint / flat / free hook.</p>
        <div className="mt-3 space-y-3">
          <PricedRow
            label="Pipe Data report"
            idPrefix="pf-pipeData"
            modeOptions={REPORT_MODE_OPTIONS}
            mode={(options.pipeDataMode as string) ?? 'off'}
            flat={options.pipeDataFlat as number | undefined}
            onMode={(m) => setOption('pipeDataMode', m)}
            onFlat={(v) => setOption('pipeDataFlat', v)}
          />
          <PricedRow
            label="Pipe Compatibility report"
            idPrefix="pf-pipeCompat"
            modeOptions={REPORT_MODE_OPTIONS}
            mode={(options.pipeCompatibilityMode as string) ?? 'off'}
            flat={options.pipeCompatibilityFlat as number | undefined}
            onMode={(m) => setOption('pipeCompatibilityMode', m)}
            onFlat={(v) => setOption('pipeCompatibilityFlat', v)}
          />
        </div>
      </div>

      {/* 3. Algorithm */}
      <div className="border-t border-[#E2E5EA] pt-5">
        <h3 className="text-base font-medium text-blueprint-blue">3. Algorithm</h3>
        <p className="mt-0.5 text-sm text-heritage-grey">Select any. Each is per joint, a flat fee, or gain-share on the savings.</p>
        <div className="mt-3 space-y-3">
          {[
            { label: 'OptiFit', modeKey: 'optiFitMode', flatKey: 'optiFitFlat', prefix: 'optiFit' },
            { label: 'OmniFit', modeKey: 'omniFitMode', flatKey: 'omniFitFlat', prefix: 'omniFit' },
          ].map(({ label, modeKey, flatKey, prefix }) => {
            const on = algoOn(modeKey);
            const mode = options[modeKey] as string | undefined;
            return (
              <div key={modeKey} className="space-y-2">
                <div className="flex flex-wrap items-end gap-4">
                  <div className="w-44">
                    <Checkbox
                      id={`pf-${modeKey}`}
                      label={label}
                      checked={on}
                      onChange={(e) => setOption(modeKey, e.target.checked ? 'perJoint' : 'off')}
                    />
                  </div>
                  {on && (
                    <div className="w-44">
                      <Select
                        label=""
                        value={mode ?? 'perJoint'}
                        options={ALGO_MODE_OPTIONS}
                        onChange={(m) => setOption(modeKey, m)}
                      />
                    </div>
                  )}
                  {on && mode === 'flat' && (
                    <div className="w-40">
                      <Input
                        id={`pf-${flatKey}`}
                        label="Flat $"
                        type="number"
                        min={0}
                        value={(options[flatKey] as number) ?? ''}
                        onChange={(e) => setOption(flatKey, num(e.target.value))}
                        placeholder="0"
                      />
                    </div>
                  )}
                </div>
                {on && mode === 'gainShare' && (
                  <div className="ml-44 flex flex-wrap items-end gap-4 rounded-md border border-[#E2E5EA] bg-page-bg p-3">
                    {GAIN_FIELDS.map(({ suffix, label: fLabel, tooltip }) => (
                      <div key={suffix} className="w-36">
                        <Input
                          id={`pf-${prefix}${suffix}`}
                          label={fLabel}
                          tooltip={tooltip}
                          type="number"
                          min={0}
                          value={(options[`${prefix}${suffix}`] as number) ?? ''}
                          onChange={(e) => setOption(`${prefix}${suffix}`, num(e.target.value))}
                          placeholder="0"
                        />
                      </div>
                    ))}
                    <p className="w-full text-xs text-heritage-grey">
                      Price/joint = (manual − tool) min ÷ 60 × $/hr × capture share, within the floor and cap (set in cost assumptions).
                    </p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <ModulePriceBreakdownPanel productId="pipeFitter" breakdown={breakdown} />

      <PipeFitterRatesDrawer
        open={ratesOpen}
        onClose={() => setRatesOpen(false)}
        config={pipefitterConfig}
        nreConfig={nreConfig}
        overrides={overrides}
        onOverrideChange={setOverride}
        onResetAll={() => onChange({ overrides: {} })}
        discountDefault={
          (breakdown?.details as { discountPercentage?: number } | undefined)?.discountPercentage ?? 0
        }
      />
    </div>
  );
};
